import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorNote } from './ErrorNote';

/**
 * A render throw stops here instead of blanking the site. `<Suspense>` catches promises, not
 * throws, so without this one broken panel takes every other panel down with it.
 */

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** What could not be drawn, in the genitive `ErrorNote` prints — "Lehe", "Päise". */
  what?: string;
  /** What the caught throw belongs to. A new value clears the note, so a boundary cannot outlive
   * the thing that broke — the routed one passes the path the reader is on. */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: unknown;
  resetKey: unknown;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): ErrorBoundaryState | null {
    if (Object.is(props.resetKey, state.resetKey)) return null;
    return { error: null, resetKey: props.resetKey };
  }

  /** The reader gets a sentence; the console keeps the stack, which is the only copy of it. */
  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return <ErrorNote error={error} what={this.props.what} crashed />;
  }
}
